import { ReactNode, useState } from "react";
import { DotLegalTable } from "@dotlegal/dotlegal-ui-components";
import { IDotLegalTableProps } from "@dotlegal/dotlegal-ui-components/dist/cjs/types/components/dotLegalTable/DotLegalTable";
import FilterBar, { FilterBarProps } from "@common/components/filterBar/FilterBar";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import { useTranslation } from "@localization/useTranslation";

export type TableFilterBarProps = Omit<FilterBarProps, "searchValue" | "searchLabel" | "onSearchChange">;

interface DotLegalTableWithFilterBarProps<T> extends Omit<IDotLegalTableProps<T>, "searchString" | "rowsPerPage"> {
    // Left out where nothing can be searched or filtered, such as a PDF, which also drops the search
    filterBar?: TableFilterBarProps;
    // Required, as the table's own default of 50 matches none of the pages
    rowsPerPage: number;
    viewContent?: ReactNode;
    rightContent?: ReactNode;
    belowToolbar?: ReactNode;
    onSearchChange?: (searchString: string) => void;
}

function DotLegalTableWithFilterBar<T>(props: DotLegalTableWithFilterBarProps<T>) {
    const { filterBar, viewContent, rightContent, belowToolbar, onSearchChange, ...tableProps } = props;
    const { translateString } = useTranslation();
    const [searchString, setSearchString] = useState("");

    return (
        <>
            {filterBar && (
                <ListPageToolbar
                    viewContent={viewContent}
                    leftContent={
                        <FilterBar
                            {...filterBar}
                            searchValue={searchString}
                            searchLabel={translateString("search")}
                            onSearchChange={(value) => {
                                setSearchString(value);
                                onSearchChange?.(value);
                            }}
                        />
                    }
                    rightContent={rightContent}
                />
            )}
            {belowToolbar}
            <DotLegalTable
                {...tableProps}
                searchString={searchString}
                emptyText={tableProps.emptyText ?? translateString("noData")}
                noSearchResultsText={tableProps.noSearchResultsText ?? translateString("noSearchResultsText")}
                paginationLabelOf={tableProps.paginationLabelOf ?? translateString("labelOf")}
            />
        </>
    );
}

export default DotLegalTableWithFilterBar;
